import { useEffect } from "react";
import styled from "styled-components";

interface ImpressumModalProps {
  onClose: () => void;
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(6, 9, 14, 0.85);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  z-index: 2000;
`;

const ModalCard = styled.div`
  width: min(620px, 100%);
  max-height: calc(100vh - 4rem);
  overflow-y: auto;
  background: radial-gradient(circle at top, rgba(51, 145, 255, 0.08), transparent 60%), var(--bg-secondary);
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 18px;
  padding: 2.5rem 2rem;
  position: relative;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin-bottom: 1.75rem;
`;

const Section = styled.section`
  margin-bottom: 1.75rem;
`;

const SectionTitle = styled.h2`
  font-size: 1rem;
  letter-spacing: 0.05em;
  margin-bottom: 0.65rem;
  color: var(--text-primary);
  text-transform: uppercase;
`;

const Paragraph = styled.p`
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin-bottom: 0.35rem;
`;

const ContactList = styled.ul`
  list-style: none;
  margin: 0.5rem 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const ContactItem = styled.li`
  font-size: 0.95rem;
`;

const ContactAnchor = styled.a`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  color: var(--text-primary);
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: var(--accent-color);
  }
`;

const IconCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: rgba(148, 163, 184, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const IconImage = styled.img`
  width: 20px;
  height: 20px;
`;

const Small = styled.p`
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-top: 1rem;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: transparent;
  color: var(--text-secondary);
  font-size: 1.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    border-color 0.2s,
    color 0.2s;

  &:hover {
    border-color: var(--accent-color);
    color: var(--accent-color);
  }
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
`;

export function ImpressumModal({ onClose }: ImpressumModalProps) {
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalCard role="dialog" aria-modal="true" aria-label="Impressum">
        <CloseButton type="button" onClick={onClose} aria-label="Close Impressum">
          ×
        </CloseButton>
        <CardBody>
          <Title>Impressum</Title>
          <Subtitle>Angaben gemäß § 5 TMG</Subtitle>

          <Section>
            <SectionTitle>Verantwortlich für den Inhalt</SectionTitle>
            <Paragraph>Konstantin Dobroliubov</Paragraph>
            <Paragraph>Käthe-Kruse-Straße 20</Paragraph>
            <Paragraph>80997 Munich</Paragraph>
            <Paragraph>Germany</Paragraph>
          </Section>

          <Section>
            <SectionTitle>Kontakt</SectionTitle>
            <ContactList>
              <ContactItem>
                <ContactAnchor href="mailto:konstantin@kuack.io">
                  <IconCircle>
                    <IconImage src="/static/icon-email.svg" alt="" />
                  </IconCircle>
                  <span>Email</span>
                </ContactAnchor>
              </ContactItem>
              <ContactItem>
                <ContactAnchor href="https://t.me/konstantin_dobroliubov" target="_blank" rel="noopener noreferrer">
                  <IconCircle>
                    <IconImage src="/static/icon-telegram.svg" alt="" />
                  </IconCircle>
                  <span>Telegram</span>
                </ContactAnchor>
              </ContactItem>
              <ContactItem>
                <ContactAnchor
                  href="https://www.linkedin.com/in/konstantin-dobroliubov/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconCircle>
                    <IconImage src="/static/icon-linkedin.svg" alt="" />
                  </IconCircle>
                  <span>LinkedIn</span>
                </ContactAnchor>
              </ContactItem>
              <ContactItem>
                <ContactAnchor href="https://github.com/kuack-konstantin" target="_blank" rel="noopener noreferrer">
                  <IconCircle>
                    <IconImage src="/static/icon-github.svg" alt="" />
                  </IconCircle>
                  <span>GitHub</span>
                </ContactAnchor>
              </ContactItem>
            </ContactList>
          </Section>

          <Section>
            <SectionTitle>Hinweis</SectionTitle>
            <Small>
              This site is operated by the person named above and is currently used to present and validate the Kuack
              startup project. A dedicated company entity (e.g. GmbH) may be added in the future. Until then, legal
              responsibility lies with the individual listed here.
            </Small>
          </Section>
        </CardBody>
      </ModalCard>
    </Overlay>
  );
}

export default ImpressumModal;
